/**
 * Bluetooth Disconnects Data Format Transformer
 *
 * Handles the format change in log file version 2.2 where bluetooth disconnects
 * changed from simple array format to structured format with device names.
 *
 * Old format (pre v2.2):
 * {
 *   points: Array<[number, number]>  // [timestamp, value]
 * }
 *
 * New format (v2.2+):
 * {
 *   columns: ["timestamp", "deviceName", "isDeliberate"],
 *   points: Array<{timestamp: number, deviceName: string, isDeliberate: boolean}>,
 *   summary: {
 *     totalDisconnects: number,
 *     errorDisconnects: number,
 *     deliberateDisconnects: number
 *   }
 * }
 */

export interface BleDisconnectsOldFormat {
  points: Array<[number, number]>;
}

export interface BleDisconnectsNewFormatPoint {
  timestamp: number;
  deviceName: string;
  isDeliberate: boolean;
}

export interface BleDisconnectsNewFormat {
  columns: string[];
  points: BleDisconnectsNewFormatPoint[];
  summary: {
    totalDisconnects: number;
    errorDisconnects: number;
    deliberateDisconnects: number;
  };
}

export type BleDisconnectsData = BleDisconnectsOldFormat | BleDisconnectsNewFormat | null | undefined;

/**
 * Check if version is 2.2 or later
 */
function isVersion22OrLater(version: string | undefined): boolean {
  if (!version) {
    return false;
  }

  const parts = version.split(".");
  const major = parseInt(parts[0] || "0", 10);
  const minor = parseInt(parts[1] || "0", 10);

  // Version 2.2 or later
  return major > 2 || (major === 2 && minor >= 2);
}

/**
 * Check if data is in new format (has columns field)
 */
function isNewFormat(data: BleDisconnectsData): data is BleDisconnectsNewFormat {
  return data !== null && data !== undefined && "columns" in data && Array.isArray(data.columns);
}

/**
 * Check if data is in old format (has points as array of tuples)
 */
function isOldFormat(data: BleDisconnectsData): data is BleDisconnectsOldFormat {
  if (!data || !("points" in data) || !Array.isArray(data.points)) {
    return false;
  }

  // Check if first point is a tuple [timestamp, value]
  const firstPoint = data.points[0];
  return Array.isArray(firstPoint) && firstPoint.length === 2;
}

/**
 * Transform new format to old format for chart compatibility
 * Only includes non-deliberate disconnects (isDeliberate: false) as these represent dropouts/errors
 * Deliberate disconnects are filtered out as they are intentional user actions
 */
function transformNewToOld(data: BleDisconnectsNewFormat): BleDisconnectsOldFormat {
  const points: Array<[number, number]> = data.points
    .filter((point) => point.isDeliberate === false) // Only include error disconnects, not deliberate ones
    .map((point) => {
      return [point.timestamp, 1]; // Each disconnect counts as 1
    });

  return {points};
}

/**
 * Normalize bluetooth disconnects data to old format for chart rendering
 *
 * @param data - Bluetooth disconnects data in either old or new format
 * @param version - Log file version string (e.g., "2.1", "2.2")
 * @returns Normalized data in old format, or null/undefined if input is null/undefined
 */
export function normalizeBleDisconnects(
  data: BleDisconnectsData,
  version: string | undefined
): BleDisconnectsOldFormat | null | undefined {
  // Handle null/undefined inputs
  if (data === null) {
    return null;
  }
  if (data === undefined) {
    return undefined;
  }

  // If it's already in old format, return as-is
  if (isOldFormat(data)) {
    return data;
  }

  // If it's in new format and version is 2.2+, transform it
  if (isNewFormat(data) && isVersion22OrLater(version)) {
    return transformNewToOld(data);
  }

  // If it's in new format but version is not 2.2+, this is unexpected
  // Log a warning and attempt transformation anyway
  if (isNewFormat(data)) {
    console.warn(
      `[BleDisconnects] Found new format data but version is ${version}. Expected v2.2+. Attempting transformation.`
    );
    return transformNewToOld(data);
  }

  // Unknown format - return empty points to avoid breaking the chart
  console.warn("[BleDisconnects] Unknown data format:", data);
  return {points: []};
}

/**
 * Get summary information from bluetooth disconnects data
 * Only available in new format (v2.2+)
 */
export function getBleDisconnectsSummary(data: BleDisconnectsData): BleDisconnectsNewFormat["summary"] | null {
  if (isNewFormat(data) && data.summary) {
    return data.summary;
  }
  return null;
}

/**
 * Get device-specific disconnect information from bluetooth disconnects data
 * Only available in new format (v2.2+)
 */
export function getBleDisconnectsByDevice(
  data: BleDisconnectsData
): Map<string, BleDisconnectsNewFormatPoint[]> | null {
  if (!isNewFormat(data)) {
    return null;
  }

  const deviceMap = new Map<string, BleDisconnectsNewFormatPoint[]>();

  for (const point of data.points) {
    const deviceName = point.deviceName;
    if (!deviceMap.has(deviceName)) {
      deviceMap.set(deviceName, []);
    }
    deviceMap.get(deviceName)?.push(point);
  }

  return deviceMap;
}
